/**
 * Environment Configuration
 * Vite exposes only the public environment name and captcha sitekey (vite.config.ts).
 * Dependencies can read import.meta.env wholesale, so service keys are excluded
 * by the build configuration as well as removed from these settings.
 */

/** The environments a build can be, as VITE_ENVIRONMENT names them. */
export const ENVIRONMENTS = ['local', 'develop', 'staging', 'production'] as const;

export type Environment = (typeof ENVIRONMENTS)[number];

/** Whether a name is one of the portal's environments. */
export const isEnvironment = (name: string): name is Environment => (ENVIRONMENTS as readonly string[]).includes(name);

/** VITE_ENVIRONMENT when it names an environment; else local in dev, production otherwise. */
function getCurrentEnvironment(): Environment {
  const envVar: string | undefined = import.meta.env.VITE_ENVIRONMENT;
  if (envVar && isEnvironment(envVar)) {
    return envVar;
  }
  if (import.meta.env.DEV) {
    return 'local';
  }
  return 'production';
}

interface EnvironmentConfig {
  gameClientUrl: string;
  backendApiUrl: string;
  /** Cloudflare Turnstile sitekey; empty string = captcha disabled (no widget) */
  turnstileSiteKey: string;
}

const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  local: {
    gameClientUrl: 'http://localhost:5173',
    backendApiUrl: 'http://localhost:3000',
    // Disabled locally by default; set VITE_TURNSTILE_SITE_KEY (e.g. the
    // Turnstile always-pass test key 1x00000000000000000000AA) to exercise it
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '',
  },
  develop: {
    gameClientUrl: 'https://ponder-flight-game-develop.playponder.com',
    backendApiUrl: 'https://portal-backend-develop.playponder.com',
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '',
  },
  staging: {
    gameClientUrl: 'https://ponder-flight-game-staging.playponder.com',
    backendApiUrl: 'https://portal-backend-staging.playponder.com',
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '',
  },
  production: {
    gameClientUrl: 'https://ponder-flight-game.playponder.com',
    backendApiUrl: 'https://portal-backend.playponder.com',
    turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '',
  },
};

/** An environment's configuration: its game client, backend and captcha key. */
export function environmentConfigFor(environment: Environment): EnvironmentConfig {
  return environmentConfigs[environment];
}

/**
 * Get the configuration for the current environment
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  return environmentConfigFor(getCurrentEnvironment());
}

/**
 * Get the game client URL for the current environment
 */
export function getGameClientUrl(): string {
  return getEnvironmentConfig().gameClientUrl;
}

/**
 * Get the Backend API base URL for the current environment
 */
export function getBackendApiUrl(): string {
  return getEnvironmentConfig().backendApiUrl;
}

/**
 * Get the Cloudflare Turnstile sitekey; empty string = captcha disabled
 */
export function getTurnstileSiteKey(): string {
  return getEnvironmentConfig().turnstileSiteKey;
}

/**
 * Whether student dev tooling (the Cheats page) is available.
 * Only in local dev and the online develop environment — students in
 * staging/production must never be able to grant themselves XP, badges,
 * or certificates.
 */
export function isStudentCheatsEnabled(): boolean {
  const env = getCurrentEnvironment();
  return env === 'local' || env === 'develop';
}

